<template>
  <div
    :role="variant === StateVariant.ERROR ? 'alert' : 'status'"
    :aria-live="variant === StateVariant.ERROR ? 'assertive' : 'polite'"
    :class="[
      'flex flex-col items-center justify-center text-center',
      isMobileView ? 'w-full max-w-[402px] gap-4 p-6' : 'w-full max-w-[480px]',
    ]"
  >
    <div class="flex flex-col items-center w-full">
      <slot name="illustration">
        <StateIllustration :illustration="illustration" />
      </slot>
    </div>
    <div class="flex flex-col gap-6 items-center w-full">
      <div
        class="flex flex-col gap-2 items-center justify-center w-full break-words"
      >
        <p class="font-bold text-2xl leading-8 text-secondary w-full">
          {{ header }}
        </p>
        <p
          v-if="subheader"
          class="font-normal text-base leading-6 text-gray w-full"
        >
          {{ subheader }}
        </p>
      </div>
      <div
        v-if="$slots.actions || primaryCtaLabel || secondaryCtaLabel"
        :class="[
          'flex items-center justify-center w-full',
          isMobileView ? 'flex-col gap-4' : 'flex-row gap-6',
        ]"
      >
        <slot name="actions">
          <Btn
            v-if="secondaryCtaLabel"
            :variant="BtnVariant.TEXT"
            :color="BtnColor.SECONDARY"
            :size="BtnSize.MD"
            :shape="BtnShape.ROUNDED"
            :class="isMobileView ? 'w-full' : ''"
            @click="emit('secondaryClick')"
          >
            {{ secondaryCtaLabel }}
          </Btn>
          <Btn
            v-if="primaryCtaLabel"
            :variant="BtnVariant.FILLED"
            :color="BtnColor.PRIMARY"
            :size="BtnSize.MD"
            :shape="BtnShape.ROUNDED"
            :class="isMobileView ? 'w-full' : ''"
            @click="emit('primaryClick')"
          >
            {{ primaryCtaLabel }}
          </Btn>
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Btn,
  BtnVariant,
  BtnColor,
  BtnSize,
  BtnShape,
} from '@/components/base/btn';
import { useWindowWidth } from '@/composables/use-window-width';
import { StateVariant, IllustrationVariant } from './state-container.constants';
import StateIllustration from './StateIllustration.vue';

type Props = {
  variant?: StateVariant;
  illustration?: IllustrationVariant;
  header: string;
  subheader?: string;
  primaryCtaLabel?: string;
  secondaryCtaLabel?: string;
};

withDefaults(defineProps<Props>(), {
  variant: StateVariant.EMPTY,
});

const emit = defineEmits<{
  (e: 'primaryClick'): void;
  (e: 'secondaryClick'): void;
}>();

const { isMobileView } = useWindowWidth();
</script>
